import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../features/auth/context/AuthContext';
import { openMidusOrWeb } from '../utils/common';

const DomainError = () => {
  const { domainError, redirectToMainDomain } = useAuthContext();
  const [countdown, setCountdown] = useState(10);
  const hasRedirected = useRef(false);
  const timerRef = useRef(null);
  
  // Start countdown immediately
  useEffect(() => {
    if (!domainError) return;
    
    console.log('Starting 10 second countdown immediately');
    
    // Start timer right away without delay
    timerRef.current = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          // Trigger redirect immediately when countdown hits zero
          if (!hasRedirected.current) {
            hasRedirected.current = true;
            console.log('Redirecting to main domain after countdown completed');
            // Use direct URL for automatic redirect (no app-try delay)
            window.location.href = 'https://midus.co' + window.location.pathname + '?from=domain_redirect';
          }
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [domainError]);
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);
  
  // Don't render anything if there's no domain error
  if (!domainError) return null;
  
  const handleGoToMidus = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    hasRedirected.current = true;
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    openMidusOrWeb(path + '?from=domain_redirect');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#111111] rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-[#444444]">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30">
            <svg 
              className="h-6 w-6 text-red-600 dark:text-red-400" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Custom Domain No Longer Active</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            This custom domain is no longer active. You still have full access to Midus and your data is safe. You'll be redirected to the main application in <span className="font-bold text-red-600 dark:text-red-400">{countdown}</span> seconds.
          </p>
          <div className="mt-4">
            <a
              href="midus://"
              onClick={handleGoToMidus}
              className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-black border border-black rounded-lg shadow-sm hover:bg-gray-800 focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-gray-200"
            >
              Go to Midus
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainError; 